import React from "react";
import { FiCalendar, FiAlertCircle, FiEye, FiPlus, FiLock } from "react-icons/fi";
import { RiMoneyDollarBoxLine } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

const SupplierOrderListItem = ({ order, isActive, onClick }) => {
  const navigate = useNavigate();

  const stageStyle = (() => {
    switch (order.stages) {
      case "Completed":
        return {
          dot: "bg-emerald-500",
          text: "text-emerald-600",
          bg: "bg-emerald-50 border-emerald-200",
        };
      case "Quotation":
        return {
          dot: "bg-blue-500",
          text: "text-blue-600",
          bg: "bg-blue-50 border-blue-200",
        };
      case "Sampling":
        return {
          dot: "bg-purple-500",
          text: "text-purple-600",
          bg: "bg-purple-50 border-purple-200",
        };
      case "Order-Fullfillment":
        return {
          dot: "bg-orange-500",
          text: "text-orange-600",
          bg: "bg-orange-50 border-orange-200",
        };
      default:
        return {
          dot: "bg-gray-400",
          text: "text-gray-500",
          bg: "bg-gray-50 border-gray-200",
        };
    }
  })();

  const varietyImg = order.productVariety?.img;
  const productImg = order.product?.img;
  const displayImg = varietyImg || productImg;

  const quotationCount = order.quotationCount ?? 0;
  const isOffer =
    order?.Source === "Offer" ||
    order?.offer?.isAttached ||
    order?.priceAndQuantity?.fixedPrice?.price != null;

  const allowedStages = ["Order", "Quotation"];
  const isCreateSessionActive =
    (!order.stages || allowedStages.includes(order.stages)) &&
    order.stages !== "Closed" &&
    order.review?.status !== "Rejected";

  const isQuotationRequired =
    quotationCount === 0 &&
    isCreateSessionActive;

  const moq = order.priceAndQuantity?.MOQ;
  const unit = order.priceAndQuantity?.standardizedUnit || "Unit";
  const currency = order.priceAndQuantity?.currency || "₹";

  const minPrice = order.priceAndQuantity?.expectedPriceRange?.min;
  const maxPrice = order.priceAndQuantity?.expectedPriceRange?.max;
  const fixedPrice = order.priceAndQuantity?.fixedPrice?.price;

  const getQuotationDisabledReason = (ord) => {
    if (!ord) return "";
    const reviewStatus = ord.review?.status || "Pending";
    const isOrderRejected = reviewStatus === "Rejected";
    const isOrderPending = reviewStatus === "Pending";
    const isValidStage = !ord.stages || allowedStages.includes(ord.stages);

    if (ord.stages === "Closed") {
      return "This order has been closed. No new quotations can be created.";
    }
    if (isOrderRejected) {
      return "This order has been rejected by admin. Quotation is not allowed.";
    }
    if (isOrderPending) {
      return "This order is under review. You can create quotation once approved by QQT.";
    }
    if (!isValidStage) {
      return "Quotation adding session has been passed for this order.";
    }
    return "";
  };

  const disabledReason = getQuotationDisabledReason(order);
  const canCreateQuotation = !disabledReason;

  const handleCreateQuotationClick = (e) => {
    e.stopPropagation();
    if (!canCreateQuotation) {
      toast.error(disabledReason, { duration: 3500, icon: "⚠️" });
      return;
    }
    navigate(`/create-quotation?orderId=${order._id}`);
  };

  const handleViewQuotationsClick = (e) => {
    e.stopPropagation();
    navigate(`/supplier-quotations?orderId=${order._id}`, {
      state: { from: "card" },
    });
  };

  return (
    <div
      onClick={onClick}
      className={`group relative rounded-xl border cursor-pointer transition-all duration-200 overflow-hidden
        ${
          isActive
            ? "border-[#772037] bg-[#772037]/[0.03] shadow-md shadow-[#772037]/10"
            : isQuotationRequired
              ? "border-pink-300 bg-white hover:border-[#772037]/60 hover:shadow-sm"
              : "border-gray-200 bg-white hover:border-[#772037]/40 hover:shadow-sm"
        }`}
    >
      <div
        className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-xl transition-all duration-200
        ${
          isActive
            ? "bg-gradient-to-b from-[#772037] to-[#CC234F]"
            : isQuotationRequired
              ? "bg-pink-400"
              : "bg-transparent group-hover:bg-[#772037]/20"
        }`}
      />

      <div className="pl-3 pr-3 pt-3 pb-3 ml-1">
        <div className="flex items-start gap-3">
          <div
            className={`w-13 h-13 rounded-xl overflow-hidden flex-shrink-0 border bg-gray-50
            ${isActive ? "border-[#772037]/20" : "border-gray-100"}`}
            style={{ width: 52, height: 52 }}
          >
            {displayImg ? (
              <img
                src={displayImg}
                alt={order.productVariety?.varietyName || order.product?.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-50 text-2xl">
                📦
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-1 flex-wrap">
              <p
                className={`text-[10px] font-bold uppercase tracking-widest mb-0.5
                ${isActive ? "text-[#772037]/70" : "text-gray-400"}`}
              >
                {order.orderNumber || `#${order._id?.slice(-6)}`}
              </p>
              {isOffer && (
                <span className="text-[9px] font-bold px-1.5 py-0.2 rounded bg-pink-50 text-[#772037] border border-pink-200">
                  🎯 Offer
                </span>
              )}
            </div>

            <h3
              className={`font-bold text-sm leading-tight truncate
              ${isActive ? "text-[#772037]" : "text-gray-800"}`}
            >
              {order.product?.name || "—"}
            </h3>

            {order.productVariety?.varietyName && (
              <p className="text-xs text-gray-500 font-medium truncate mt-0.5">
                {order.productVariety.varietyName}
                {order.productVariety?.grade && (
                  <span className="ml-1 text-[9px] font-bold px-1 py-px rounded bg-gray-100 text-gray-500 uppercase">
                    {order.productVariety.grade}
                  </span>
                )}
              </p>
            )}

            {/* Order Quantity Text & Quotation Badges */}
            <div className="flex items-center gap-1.5 mt-2 flex-wrap">
              {moq != null && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-gray-100 text-gray-700 border border-gray-200">
                  <span className="text-gray-400 font-medium">Order Quantity:</span>{" "}
                  {moq.toLocaleString()} {unit}
                </span>
              )}

              {isQuotationRequired ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-pink-50 text-pink-700 border border-pink-200 animate-pulse">
                  <FiAlertCircle className="text-[10px]" /> Quotation Required
                </span>
              ) : quotationCount > 0 ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-50 text-blue-600 border border-blue-200">
                  {quotationCount} {quotationCount === 1 ? "Quotation" : "Quotations"}
                </span>
              ) : null}
            </div>
          </div>
        </div>

        {/* Price & Stage row */}
        <div className="my-2 border-t border-dashed border-gray-100" />

        <div className="flex items-center justify-between gap-2 flex-wrap">
          {/* Target / Expected Price */}
          <div className="text-[11px] font-semibold text-gray-700">
            {isOffer && fixedPrice != null ? (
              <span className="text-[#772037] font-bold">
                {currency} {fixedPrice.toLocaleString()} / {unit}
              </span>
            ) : minPrice != null && maxPrice != null && (minPrice > 0 || maxPrice > 0) ? (
              <span className="text-[#772037] font-bold">
                {currency} {minPrice.toLocaleString()} - {maxPrice.toLocaleString()}{" "}
                <span className="text-gray-400 font-normal text-[10px]">/ {unit}</span>
              </span>
            ) : (
              <div className="flex items-center gap-1 text-gray-400">
                <FiCalendar className="text-xs" />
                <span className="text-[10px] font-medium">
                  {new Date(order.createdAt).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "short",
                  })}
                </span>
              </div>
            )}
          </div>

          {/* Stage badge */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-bold border ${stageStyle.bg} ${stageStyle.text}`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${stageStyle.dot} ${
                  order.stages !== "Completed" && order.stages !== "Closed"
                    ? "animate-pulse"
                    : ""
                }`}
              />
              {order.stages?.replace(/_/g, " ") || "Pending"}
            </span>
          </div>
        </div>

        {order.category?.name && (
          <p className="mt-1.5 text-[10px] text-gray-400 font-medium truncate">
            {[order.category?.name, order.subCategory?.name]
              .filter(Boolean)
              .join(" › ")}
          </p>
        )}

        {/* ── MOBILE ACTION BUTTONS (View Details & Create Quotation / Quotations Count) ── */}
        <div className="md:hidden mt-3 pt-2.5 border-t border-gray-100 flex items-center gap-2">
          <button
            type="button"
            onClick={onClick}
            className="flex-1 py-2 px-2.5 rounded-xl text-xs font-bold bg-gray-50 hover:bg-gray-100 text-gray-800 border border-gray-200 flex items-center justify-center gap-1.5 transition shadow-2xs"
          >
            <FiEye className="text-xs text-primaryColor" />
            <span>View Details</span>
          </button>

          {quotationCount > 0 ? (
            <button
              type="button"
              onClick={handleViewQuotationsClick}
              className="flex-1 py-2 px-2.5 rounded-xl text-xs font-bold bg-[#772037]/10 hover:bg-[#772037]/15 text-[#772037] border border-[#772037]/20 flex items-center justify-center gap-1.5 transition shadow-2xs"
            >
              <RiMoneyDollarBoxLine className="text-xs" />
              <span>Quotations ({quotationCount})</span>
            </button>
          ) : order.stages === "Closed" ? (
            <button
              type="button"
              disabled
              className="flex-1 py-2 px-2.5 rounded-xl text-xs font-bold bg-gray-100 text-gray-400 border border-gray-200 flex items-center justify-center gap-1.5 cursor-not-allowed opacity-75"
            >
              <FiLock className="text-xs text-red-500" />
              <span>Order Closed</span>
            </button>
          ) : isCreateSessionActive ? (
            <button
              type="button"
              onClick={handleCreateQuotationClick}
              className="flex-1 py-2 px-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition shadow-2xs bg-primaryColor text-white hover:bg-primaryColor/90 shadow-primaryColor/20"
            >
              <FiPlus className="text-xs" />
              <span>Create Quotation</span>
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default SupplierOrderListItem;
